import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteVehicle, getVehicles } from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../helpers/FilterSearchHelper";

import { Table, Button, message, Modal, Input, Dropdown, Menu } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import styles from "./Vehicles.module.scss";
import { useNavigate } from "react-router-dom";

const Vehicles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [nameFilterValue, setNameFilterValue] = useState("");
  const debouncedNameFilterValue = useDebouncedValue(nameFilterValue);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_VEHICLES)) fetchVehicles();
  }, [debouncedNameFilterValue]);

  const fetchVehicles = async (pagination = actualPage) => {
    const requestId = beginRequest();
    const response = await getVehicles(
      pagination,
      { name: debouncedNameFilterValue },
      "vehicleProjectionList"
    );

    if (isStaleRequest(requestId)) return;

    setData(response.vehicles);
    setTotalSize(response.totalElements);
    setLoading(false);
  };

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    const deleteStatus = await deleteVehicle(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchVehicles();
    setLoading(false);
  };

  const handlePagination = (page: number) => {
    fetchVehicles(page);
    setActualPage(page);
  };

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
    },
    {
      title: t("Common-Code"),
      dataIndex: "code",
    },
    {
      title: t("Common-Type"),
      key: "typeVehicle",
      render: (vehicle: any) => vehicle.typeVehicle.description_es,
    },
    {
      title: t("Common-Capacity"),
      dataIndex: "maxCapacity",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                icon={
                  hasPermission(privilegesEnum.WRITE_VEHICLES) ? (
                    <EditOutlined />
                  ) : (
                    <EyeOutlined />
                  )
                }
                onClick={() => navigate(`/admin/vehicles/${record.id}/view`)}
              >
                {t(
                  hasPermission(privilegesEnum.WRITE_VEHICLES)
                    ? "Common-Edit"
                    : "Common-View"
                )}
              </Menu.Item>
              {hasPermission(privilegesEnum.DELETE_VEHICLES) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-Vehicles")}</h1>
        {hasPermission(privilegesEnum.WRITE_VEHICLES) && (
          <div className={styles.actionButtonsWrapper}>
            <Button
              onClick={() => {
                navigate("/admin/vehicles/create");
              }}
              className={styles.newButton}
              type="primary"
              icon={<PlusOutlined />}
            />
          </div>
        )}
      </div>
      <div className={styles.contentWrapper}>
        {hasPermission(privilegesEnum.READ_VEHICLES) && (
          <div className={styles.tableWrapper}>
            <div className={styles.headerTableWrapper}>
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Input
                    placeholder={t("Common-SearchByName")}
                    className={styles.filterInput}
                    onChange={(e) => {
                      setNameFilterValue(e.target.value);
                      setActualPage(1);
                    }}
                    value={nameFilterValue}
                  />
                </div>
              </div>
            </div>
            <Table
              size="small"
              rowKey={(record) => record.id}
              className={styles.table}
              loading={loading}
              columns={columns}
              dataSource={data}
              pagination={{
                total: totalSize,
                showSizeChanger: false,
                onChange: handlePagination,
                current: actualPage,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Vehicles;
